import {
  AttachFile,
  AutoAwesomeOutlined,
  Check,
  ContactSupport,
  DeleteOutline,
  EditOutlined,
  EmailOutlined,
  MarkEmailReadOutlined,
  SendOutlined,
  ShareOutlined,
  ThumbDownOffAlt,
} from '@icons';
import { ActivityType, SubmissionStatus } from 'enums';
import { messages } from 'i18n';
import { ActivityLog } from 'types';
import { SubmissionViewMode } from 'broker/components/SubmissionsView/utils';
import { submissionStatusConfig } from 'broker/configuration-mappers/submission-status-config';
import { ActivityTypeDisplayConfig } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/ActivityLog';
import { CarrierSubmissionUpdateActivity } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/CarrierSubmissionActivity/CarrierSubmissionUpdateActivity';
import { SubmissionLevelActivities } from './types';

const addBasedOnPrimarySuffix = (displayText: string, activityLog: ActivityLog) =>
  displayText +
  (activityLog.quote?.primaryQuote
    ? `, ${messages.general.xsOfMarket(activityLog.quote.primaryQuote.marketName ?? '')} `
    : '');

function listMarkets(activityLog: ActivityLog) {
  return activityLog.groupedActivities?.map((activity) => activity.submissionMarket?.marketName).join(', ') ?? '';
}

export const submissionActivityTypeConfig: Record<SubmissionLevelActivities, ActivityTypeDisplayConfig> = {
  [ActivityType.AddedManually]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.AddedManually],
    iconTooltip: listMarkets,
    icon: Check,
  },
  [ActivityType.AddedAsIncumbent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.AddedAsIncumbent],
    iconTooltip: listMarkets,
    icon: Check,
  },
  [ActivityType.MarketDeclined]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.MarketDeclined],
    icon: ThumbDownOffAlt,
  },
  [ActivityType.QuoteIndication]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.QuoteIndication],
    icon: EmailOutlined,
  },
  [ActivityType.InformationRequested]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.InformationRequested],
    icon: ContactSupport,
  },
  [ActivityType.RequestForQuote]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.RequestForQuote],
    iconTooltip: listMarkets,
    icon: MarkEmailReadOutlined,
  },
  [ActivityType.MarketEmailSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.MarketEmailSent],
    icon: MarkEmailReadOutlined,
    iconTooltip: listMarkets,
  },
  [ActivityType.CustomerEmailSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.CustomerEmailSent],
    icon: SendOutlined,
  },

  [ActivityType.InsuredEmailSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.InsuredEmailSent],
    icon: SendOutlined,
  },

  [ActivityType.ProposalSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.ProposalSent],
    icon: SendOutlined,
  },

  [ActivityType.MarketingReportSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.MarketingReportSent],
    icon: ShareOutlined,
  },
  [ActivityType.InformationRequestSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.InformationRequestSent],
    icon: ShareOutlined,
  },
  [ActivityType.SubmissionCreated]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.SubmissionCreated],
    icon: Check,
  },
  [ActivityType.SubmissionUpdated]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.SubmissionUpdated],
    icon: EditOutlined,
  },
  [ActivityType.ProposalUpdated]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.ProposalUpdated],
    icon: EditOutlined,
  },

  [ActivityType.CustomerEmailReceived]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.CustomerEmailReceived],
    icon: EmailOutlined,
  },
  [ActivityType.MarketEmailReceived]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.MarketEmailReceived],
    icon: EmailOutlined,
  },
  [ActivityType.QuoteAttached]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.QuoteAttached](activityLog),
        activityLog,
      ),
    icon: AttachFile,
  },
  [ActivityType.SubmissionStatusUpdated]: {
    displayText: (activityLog) => {
      const statusChangedField = activityLog.metadata?.changedFields?.find(
        (fieldChanged) => fieldChanged.field === 'status',
      );
      const oldValue = statusChangedField?.oldValue
        ? submissionStatusConfig[statusChangedField.oldValue as SubmissionStatus]?.text(SubmissionViewMode.General)
        : '';
      const newValue = statusChangedField?.newValue
        ? submissionStatusConfig[statusChangedField.newValue as SubmissionStatus]?.text(SubmissionViewMode.General)
        : '';
      return messages.submissionActivityLog.activitiesTitles[ActivityType.SubmissionStatusUpdated](oldValue, newValue);
    },
    icon: EditOutlined,
  },
  [ActivityType.QuoteNotAccepted]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.QuoteNotAccepted](activityLog),
        activityLog,
      ),
    icon: ThumbDownOffAlt,
  },
  [ActivityType.QuoteRemoved]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.QuoteRemoved](activityLog),
        activityLog,
      ),
    icon: DeleteOutline,
  },
  [ActivityType.QuoteUpdated]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.QuoteUpdated](activityLog),
        activityLog,
      ),
    icon: AttachFile,
  },
  [ActivityType.BinderAttached]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.BinderAttached](activityLog),
        activityLog,
      ),
    icon: AttachFile,
  },
  [ActivityType.BinderUpdated]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.BinderUpdated](activityLog),
        activityLog,
      ),
    icon: AttachFile,
  },
  [ActivityType.BinderRemoved]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.BinderRemoved](activityLog),
        activityLog,
      ),
    icon: DeleteOutline,
  },
  [ActivityType.PolicyIssued]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.PolicyIssued](activityLog),
        activityLog,
      ),
    icon: AttachFile,
  },
  [ActivityType.PolicyRemoved]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.PolicyRemoved](activityLog),
        activityLog,
      ),
    icon: DeleteOutline,
  },
  [ActivityType.PolicyUpdated]: {
    displayText: (activityLog) =>
      addBasedOnPrimarySuffix(
        messages.submissionActivityLog.activitiesTitles[ActivityType.PolicyUpdated](activityLog),
        activityLog,
      ),
    icon: AttachFile,
  },
  [ActivityType.ArfSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.ArfSent],
    icon: Check,
  },
  [ActivityType.ArfRequest]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.ArfRequest],
    icon: Check,
  },
  [ActivityType.SolicitationEmailSent]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.SolicitationEmailSent],
    icon: SendOutlined,
  },
  [ActivityType.CarrierSubmissionUpdated]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.CarrierSubmissionUpdated],
    detailContent: (activityLog) => <CarrierSubmissionUpdateActivity activityLog={activityLog} />,
    icon: EditOutlined,
  },
  [ActivityType.CarrierSubmissionExtracted]: {
    displayText: messages.submissionActivityLog.activitiesTitles[ActivityType.CarrierSubmissionExtracted],
    detailContent: (activityLog) => <CarrierSubmissionUpdateActivity activityLog={activityLog} />,
    icon: AutoAwesomeOutlined,
  },
};
